// Live nebula on the GPU.
//
// A fragment shader evaluates a domain-warped noise field per pixel every
// frame, so the clouds churn continuously, and a class change is nothing
// more than easing a few uniforms: the warp offset, which carries the
// field to a different configuration, and the four palette colours. The
// caller owns the easing; this module only draws whatever state it is
// handed. Two cloud layers are composed in the shader at different scales
// and parallax depths.
//
// Rendered at a fixed low resolution and stretched by CSS, which doubles
// as a free blur. Returns null where WebGL is unavailable (tests, very old
// GPUs); NightSky falls back to a still painting.

const VERT = `
attribute vec2 aPos;
varying vec2 vUv;
void main() {
  vUv = aPos * 0.5 + 0.5;
  gl_Position = vec4(aPos, 0.0, 1.0);
}`;

const FRAG = `
precision highp float;
varying vec2 vUv;
uniform vec2 uRes;
uniform float uTime;
uniform vec2 uShift;
uniform vec2 uPar;
uniform vec3 uDust;
uniform vec3 uMid;
uniform vec3 uAccent;
uniform vec3 uRidge;
uniform float uIntensity;
uniform sampler2D uFigures;
uniform float uFigureWeights[5];
uniform vec4 uFigureRect;
uniform float uFigureStrength;

float hash(vec2 p) {
  p = fract(p * vec2(123.34, 456.21));
  p += dot(p, p + 45.32);
  return fract(p.x * p.y);
}
float vnoise(vec2 p) {
  vec2 i = floor(p);
  vec2 f = fract(p);
  f = f * f * (3.0 - 2.0 * f);
  float a = hash(i);
  float b = hash(i + vec2(1.0, 0.0));
  float c = hash(i + vec2(0.0, 1.0));
  float d = hash(i + vec2(1.0, 1.0));
  return mix(mix(a, b, f.x), mix(c, d, f.x), f.y);
}
float fbm(vec2 p) {
  float v = 0.0;
  float a = 0.5;
  mat2 m = mat2(1.6, 1.2, -1.2, 1.6);
  for (int i = 0; i < 5; i++) {
    v += a * vnoise(p);
    p = m * p;
    a *= 0.5;
  }
  return v;
}

vec3 figureField(vec2 p) {
  float inside = step(0.0, p.x) * step(p.x, 1.0) * step(0.0, p.y) * step(p.y, 1.0);
  p = clamp(p, vec2(0.002), vec2(0.998));
  vec3 density = vec3(0.0);
  for (int i = 0; i < 5; i++) {
    density += texture2D(uFigures, vec2(p.x, (p.y + float(i)) / 5.0)).rgb * uFigureWeights[i];
  }
  return density * inside;
}

// One cloud layer: warped noise for shape, a slower field for hue.
vec4 layer(vec2 uv, vec2 shift, float scale, float t, vec2 par, float seed, float guide, float formation, float hueBias) {
  vec2 p = uv * scale + shift + par + seed;
  vec2 q = vec2(fbm(p + t * 0.020), fbm(p + vec2(5.2, 1.3) - t * 0.016));
  vec2 r = vec2(fbm(p + 1.7 * q + vec2(1.7, 9.2) + t * 0.028), fbm(p + 1.7 * q + vec2(8.3, 2.8) - t * 0.022));
  float n = fbm(p + 1.9 * r);
  // Cloud structure crosses the target uninterrupted. Never use the
  // drawing's derivatives to bend UVs: that produces refractive rims.
  float wisps = fbm(p * 9.0 + r * 2.0 - t * 0.035);
  float grain = fbm(p * 27.0 + q - t * 0.025);
  n += (wisps - 0.5) * 0.16 + (grain - 0.5) * 0.05;
  // Raised threshold: the gas gathers in islands with open night between
  // them, rather than a haze over everything.
  float cloud = smoothstep(0.42, 0.82, n);
  cloud *= 0.8 + wisps * 0.35 + grain * 0.15;
  // Gather existing gas into a loose volume. Empty parts of the noise
  // remain empty even at maximum formation; no mask-shaped solid body.
  cloud *= mix(1.0, 1.0 + guide * 1.2, formation);
  float ridge = pow(smoothstep(0.58, 0.92, n), 2.0);
  // Two pigments, not one blend: the hue field is stretched and the
  // crossover kept narrow, so the cloud reads as patches of the class
  // colour beside patches of its companion, meeting along soft seams.
  float hueF = (fbm(p * 0.55 + r * 0.6 + vec2(3.1, 7.7)) - 0.5) * 1.8 + 0.5;
  vec3 pigment = mix(uMid, uAccent, smoothstep(0.40, 0.60, hueF + hueBias));
  vec3 col = mix(uDust, pigment, smoothstep(0.34, 0.60, n));
  col = mix(col, uRidge, ridge * 0.65);
  float alpha = 1.0 - exp(-(cloud * 0.8 + ridge * 0.55));
  return vec4(col, alpha);
}


void main() {
  vec2 uv = vUv;
  uv.x *= uRes.x / uRes.y;
  float t = uTime;
  float total = 0.0;
  for (int i = 0; i < 5; i++) total += uFigureWeights[i];
  float formation = clamp(total * uFigureStrength, 0.0, 1.0);
  vec2 screen = vec2(vUv.x, 1.0 - vUv.y);
  vec2 fp = (screen - uFigureRect.xy) / uFigureRect.zw;
  // The same slow domain warp that gives the nebula its cloudy edges also
  // disturbs its target; no exact contours or independent glow survive.
  fp += (vec2(fbm(uv * 3.0 + t * 0.015), fbm(uv * 3.0 + 8.0 - t * 0.012)) - 0.5) * 0.10;
  float guide = figureField(fp).r / max(total, 0.001);
  vec4 a = layer(uv, uShift, 0.95, t, uPar * 0.020, 0.0, guide, formation, 0.10);
  vec4 b = layer(uv, -uShift * 0.7 + vec2(3.0, 1.0), 1.55, t * 0.8, uPar * 0.034, 11.0, guide, formation * 0.6, -0.12);
  // The far layer sits under the near one.
  vec3 col = a.rgb * a.a;
  float alpha = a.a;
  col += b.rgb * b.a * (1.0 - alpha) * 0.8;
  alpha += b.a * (1.0 - alpha) * 0.8;
  // The cloud may sit anywhere; only the far corners soften, so it never
  // reads as an oval pinned to the centre.
  vec2 c = vUv - vec2(0.5, 0.5);
  float edge = 1.0 - 0.55 * smoothstep(0.62, 1.05, length(c * vec2(1.0, 1.2)) * 1.25);
  col *= edge * uIntensity;
  alpha *= edge * uIntensity;
  gl_FragColor = vec4(col, alpha);
}`;

function compile(gl, type, src) {
  const sh = gl.createShader(type);
  gl.shaderSource(sh, src);
  gl.compileShader(sh);
  if (!gl.getShaderParameter(sh, gl.COMPILE_STATUS)) {
    const log = gl.getShaderInfoLog(sh);
    gl.deleteShader(sh);
    throw new Error(log || 'shader failed to compile');
  }
  return sh;
}

export function createNebulaRenderer(canvas) {
  let gl;
  try {
    gl = canvas.getContext('webgl', { alpha: true, premultipliedAlpha: true, antialias: false, depth: false, stencil: false });
  } catch {
    gl = null;
  }
  if (!gl) return null;
  let program;
  try {
    program = gl.createProgram();
    gl.attachShader(program, compile(gl, gl.VERTEX_SHADER, VERT));
    gl.attachShader(program, compile(gl, gl.FRAGMENT_SHADER, FRAG));
    gl.linkProgram(program);
    if (!gl.getProgramParameter(program, gl.LINK_STATUS)) throw new Error(gl.getProgramInfoLog(program) || 'link failed');
  } catch (err) {
    console.warn('[nebula] WebGL unavailable, falling back to a still painting:', err?.message || err);
    return null;
  }
  gl.useProgram(program);
  const buf = gl.createBuffer();
  gl.bindBuffer(gl.ARRAY_BUFFER, buf);
  gl.bufferData(gl.ARRAY_BUFFER, new Float32Array([-1, -1, 3, -1, -1, 3]), gl.STATIC_DRAW);
  const aPos = gl.getAttribLocation(program, 'aPos');
  gl.enableVertexAttribArray(aPos);
  gl.vertexAttribPointer(aPos, 2, gl.FLOAT, false, 0, 0);
  const u = {};
  for (const name of ['uRes', 'uTime', 'uShift', 'uPar', 'uDust', 'uMid', 'uAccent', 'uRidge', 'uIntensity', 'uFigures', 'uFigureWeights[0]', 'uFigureRect', 'uFigureStrength']) {
    u[name] = gl.getUniformLocation(program, name);
  }
  gl.clearColor(0, 0, 0, 0);
  const figures = gl.createTexture();
  gl.bindTexture(gl.TEXTURE_2D, figures);
  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR);
  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.LINEAR);
  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);
  gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, 1, 1, 0, gl.RGBA, gl.UNSIGNED_BYTE, new Uint8Array([0, 0, 0, 255]));
  gl.uniform1i(u.uFigures, 0);

  return {
    setFigures(atlas) {
      gl.bindTexture(gl.TEXTURE_2D, figures);
      gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, gl.RGBA, gl.UNSIGNED_BYTE, atlas);
    },
    resize(w, h) {
      canvas.width = w;
      canvas.height = h;
      gl.viewport(0, 0, w, h);
      gl.uniform2f(u.uRes, w, h);
    },
    // state: { time, shift:[x,y], par:[x,y], dust, mid, accent, ridge (each [r,g,b] 0..1), intensity }
    render(state) {
      gl.uniform1f(u.uTime, state.time);
      gl.uniform2f(u.uShift, state.shift[0], state.shift[1]);
      gl.uniform2f(u.uPar, state.par[0], state.par[1]);
      gl.uniform3fv(u.uDust, state.dust);
      gl.uniform3fv(u.uMid, state.mid);
      gl.uniform3fv(u.uAccent, state.accent);
      gl.uniform3fv(u.uRidge, state.ridge);
      gl.uniform1f(u.uIntensity, state.intensity);
      gl.uniform1fv(u['uFigureWeights[0]'], state.figureWeights || [0, 0, 0, 0, 0]);
      gl.uniform4fv(u.uFigureRect, state.figureRect || [0, 0, 1, 1]);
      gl.uniform1f(u.uFigureStrength, state.figureStrength ?? 1);
      gl.clear(gl.COLOR_BUFFER_BIT);
      gl.drawArrays(gl.TRIANGLES, 0, 3);
    },
    dispose() {
      gl.deleteBuffer(buf);
      gl.deleteTexture(figures);
      gl.deleteProgram(program);
      gl.getExtension('WEBGL_lose_context')?.loseContext();
    },
  };
}

// Palette for a class colour: violet dust, a teal-leaning mid tone, the
// accent itself, and a warm ridge highlight. Values 0..1.
export function paletteFor(accentHex) {
  const m = /^#?([0-9a-f]{2})([0-9a-f]{2})([0-9a-f]{2})$/i.exec(accentHex || '');
  const [r, g, b] = m ? [parseInt(m[1], 16) / 255, parseInt(m[2], 16) / 255, parseInt(m[3], 16) / 255] : [0.56, 0.66, 0.78];
  const companions = {
    '#8fd3a4': [0.48, 0.38, 0.78],
    '#c9a0e0': [0.25, 0.68, 0.72],
    '#e0736a': [0.34, 0.43, 0.80],
    '#82b4e6': [0.68, 0.40, 0.75],
    '#e8c87a': [0.32, 0.58, 0.72],
  };
  const companion = companions[accentHex?.toLowerCase()] || [0.48, 0.38, 0.74];
  return {
    dust: [r * 0.30 + 0.18, g * 0.18 + 0.06, b * 0.35 + 0.34],
    mid: companion,
    accent: [Math.min(1, r * 1.05), Math.min(1, g * 1.05), Math.min(1, b * 1.05)],
    ridge: [0.98, 0.76 + g * 0.08, 0.40 + b * 0.1],
  };
}

// Each class carries its own warp offset, so its clouds have their own
// shape and the field visibly flows when the class under the eye changes.
export function shiftFor(key) {
  let h = 2166136261;
  for (const ch of String(key || 'sky')) h = Math.imul(h ^ ch.charCodeAt(0), 16777619);
  const a = ((h >>> 0) % 1000) / 1000;
  const b = (((h >>> 10) >>> 0) % 1000) / 1000;
  return [a * 6.0 - 3.0, b * 6.0 - 3.0];
}
